/**
 * Fetches the complementary-products section and swaps it in.
 *
 * The `recommendations` Liquid object is only filled when Shopify renders a section
 * through the product recommendations endpoint, so the section arrives empty with the
 * page and asks for its own contents here. Loading it on first approach rather than
 * immediately keeps it off the critical path — it sits below the product.
 *
 * Any failure leaves the element empty, which is the same as having no complementary
 * products set: the block simply does not appear.
 */
(function () {
  class ComplementaryProducts extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;

      const load = () => {
        if (this.loaded) return;
        this.loaded = true;
        fetch(this.dataset.url)
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((text) => {
            // The endpoint returns the whole section wrapper; take its inner markup so
            // this element is not nested inside a second copy of itself.
            const fetched = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('complementary-products');
            if (!fetched || !fetched.innerHTML.trim()) return;
            this.innerHTML = fetched.innerHTML;
            // Drives the full-width divider in CSS — set only when there is content.
            this.classList.add('is-populated');
          })
          .catch(() => {});
      };

      if (!window.IntersectionObserver) {
        load();
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          load();
        },
        { rootMargin: '400px' }
      );
      io.observe(this);
    }
  }

  if (!customElements.get('complementary-products')) {
    customElements.define('complementary-products', ComplementaryProducts);
  }
})();
